import React, { useState } from 'react';
import { Badge } from '@/ui/components/badge';
import { Button } from '@/ui/components/button';
import { Input } from '@/ui/components/input';
import { ChevronRightIcon, HelpIcon, RefreshIcon } from '@/ui/icons';
import IncomeModal from './incomeModal';
import { type AmountFieldController, useAmountField } from './useAmountField';
import {
  calculateAngelTax,
  type AngelTaxResult,
} from '../../models/questions/angelTaxCalculator';

// エンジェル税制シミュレーターの入力フォーム。シミュレーターのページとエンジェル税制 LP で共有する。
// 計算結果の表示と、結果の位置へのスクロールは呼び出し側が持つ

interface Props {
  // 計算済みなら、送信ボタンを「再度シミュレーションする」にする
  calculated: boolean;
  // 入力がそろって計算できたときに、計算結果を渡す
  onCalculate: (result: AngelTaxResult) => void;
}

interface AmountFieldProps {
  id: string;
  label: string;
  placeholder: string;
  field: Pick<AmountFieldController, 'required' | 'error' | 'inputProps'>;
  // ラベル横の補助（総所得金額の「？」ボタン）
  labelAddon?: React.ReactNode;
  // 入力欄の下に出す注記
  notes?: string[];
}

function AmountField({
  id,
  label,
  placeholder,
  field: { required, error, inputProps },
  labelAddon,
  notes,
}: AmountFieldProps) {
  return (
    // 1 行目は上の罫線を持たない。SP は上の余白もなし
    <div className="tw:flex tw:flex-col tw:gap-2 tw:border-t tw:border-border tw:py-4 tw:first:border-t-0 tw:first:pt-0 tw:md:py-3 tw:md:first:pt-3">
      <div className="tw:flex tw:flex-col tw:gap-2 tw:md:flex-row tw:md:items-center tw:md:justify-between">
        <div className="tw:flex tw:items-center tw:gap-2 tw:md:gap-3">
          <label
            htmlFor={id}
            className="tw:m-0 tw:flex tw:items-center tw:gap-2 tw:text-sm tw:leading-none tw:font-semibold tw:md:gap-3 tw:md:text-base"
          >
            <Badge
              variant={required ? 'default' : 'secondary'}
              className="tw:font-noto"
            >
              {required ? '必須' : '任意'}
            </Badge>
            {label}
          </label>
          {labelAddon}
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          <Input
            id={id}
            type="text"
            inputMode="numeric"
            className="tw:w-full tw:text-right tw:md:w-60"
            placeholder={placeholder}
            {...inputProps}
          />
          <span className="tw:text-sm tw:leading-none tw:font-semibold tw:tracking-text tw:md:text-base">
            円
          </span>
        </div>
      </div>
      {error != null && (
        <p
          role="alert"
          className="tw:m-0 tw:text-sm tw:font-semibold tw:text-destructive tw:md:text-right"
        >
          {error}
        </p>
      )}
      {notes != null && (
        <ul className="tw:m-0 tw:list-none tw:p-0 tw:font-noto tw:text-2xs tw:leading-normal tw:tracking-text tw:text-muted-foreground">
          {notes.map(note => (
            <li key={note}>※{note}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AngelTaxForm({ calculated, onCalculate }: Props) {
  const income = useAmountField({ required: true });
  const investment = useAmountField({ required: true });
  const gain = useAmountField({ required: false });
  const gainByOutsideStock = useAmountField({ required: false });
  const [showIncomeModal, setShowIncomeModal] = useState(false);

  const handleSubmit = () => {
    const fields = [income, investment, gain, gainByOutsideStock];
    const requiredEmptyFields = fields.filter(field => field.isRequiredEmpty);
    requiredEmptyFields.forEach(field => field.showRequiredError());
    // 未入力や入力エラーの欄があるうちは計算しない（表示中の結果は変えない）
    if (
      requiredEmptyFields.length > 0 ||
      fields.some(field => field.error != null)
    ) {
      return;
    }

    // 譲渡益は任意で、空は 0 として計算する。何で計算したかが分かるように入力欄にも 0 を補完する
    gain.fillZeroIfEmpty();
    gainByOutsideStock.fillZeroIfEmpty();
    onCalculate(
      calculateAngelTax({
        income: income.amount,
        investment: investment.amount,
        gain: gain.amount,
        gainByOutsideStock: gainByOutsideStock.amount,
      }),
    );
  };

  return (
    <>
      <form
        className="tw:m-0 tw:flex tw:w-full tw:flex-col tw:gap-6 tw:md:gap-10"
        onSubmit={e => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="tw:flex tw:flex-col">
          <AmountField
            id="income"
            label="総所得金額"
            placeholder="例, 10000000"
            field={income}
            labelAddon={
              // Preflight を読み込んでいないため button の UA 既定（枠線・背景）を打ち消す
              <button
                type="button"
                aria-label="総所得金額とは？"
                className="tw:m-0 tw:flex tw:cursor-pointer tw:border-0 tw:bg-transparent tw:p-0 tw:text-input-icon"
                onClick={() => setShowIncomeModal(true)}
              >
                <HelpIcon className="tw:size-6" />
              </button>
            }
          />
          <AmountField
            id="investment"
            label="エンジェル税制適用会社への投資額"
            placeholder="例, 500000"
            field={investment}
          />
          <AmountField
            id="gain"
            label="上場株式の売買による譲渡益"
            placeholder="例, 1000000"
            field={gain}
            notes={[
              '上場株式の売買において譲渡損の場合は0を記入してください。',
              '上場株式に関わる譲渡損失の繰越がない前提で記入してください。',
              '株式譲渡は特定口座（源泉あり）で取引されていることが前提です。',
            ]}
          />
          <AmountField
            id="gain-by-outside-stock"
            label="非上場株式の売買による譲渡益"
            placeholder="例, 1000000"
            field={gainByOutsideStock}
            notes={[
              '非上場株式の売買において譲渡損の場合は0を記入してください。',
              '非上場株式に関わる譲渡損失の繰越がない前提で記入してください。',
            ]}
          />
        </div>
        {/* 計算するのシェブロンだけ右端に固定する（data-icon） */}
        {calculated ? (
          <Button
            type="submit"
            size="lg"
            variant="outline"
            className="tw:mx-auto tw:h-13 tw:w-65 tw:font-noto tw:md:h-15 tw:md:w-81"
          >
            再度シミュレーションする
            <RefreshIcon />
          </Button>
        ) : (
          <Button
            type="submit"
            size="lg"
            className="tw:mx-auto tw:h-13 tw:w-65 tw:font-noto tw:md:h-15 tw:md:w-81"
          >
            計算する
            <ChevronRightIcon data-icon="end" />
          </Button>
        )}
      </form>

      <IncomeModal
        show={showIncomeModal}
        onClose={() => setShowIncomeModal(false)}
      />
    </>
  );
}
